/** @jsxImportSource react */
import React, { useMemo, useState, FC } from 'react'
import { Navigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '../components/ui/Toast';
import { useAuth } from '../contexts/auth'
import { useTranslation } from '../i18n'
import { User, UserRole } from '../types'
import { api } from '../services/api'
import { COUNTRY_CODES } from '../constants';
import {
  Edit,
  Unlock,
  CheckCircle,
  Clock,
  Search,
  UserPlus,
  Filter,
  ChevronDown,
  Mail,
  Phone,
  ShieldCheck,
  UserCog,
  AlertTriangle,
  UserCheck,
  Lock,
  Tag,
  MapPin,
  XCircle,
  Save,
  Trash2,
} from 'lucide-react'
import { Modal } from '../components/ui/Modal';
import { AlertModal } from '../components/ui/AlertModal';
import { Button } from '../components/ui/Button';

interface UserFormProps {
  user: User | null;
  onClose: () => void;
  // onSave is only for creating new users now
  onSave?: (user: Partial<User>) => void;
  isSaving?: boolean;
}

// User Form Component
const UserForm: FC<UserFormProps> = ({ user, onClose, onSave, isSaving }) => {
  const { t, dir } = useTranslation();
  const isEditMode = !!user;
  const queryClient = useQueryClient();

  // Form Data
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(user?.role || UserRole.Client);
  const [teCode, setTeCode] = useState(user?.teCode || '');
  const [countryCode, setCountryCode] = useState(user?.countryCode || '+964');
  const [phone, setPhone] = useState(user?.phone || '');
  const [city, setCity] = useState(user?.city || '');

  const updateUserMutation = useMutation({
    mutationFn: (allUserData: Partial<User> & { id: string }) => {
        const { id, ...data } = allUserData;
        return api.updateUser(id, data);
    },
    onSuccess: (data) => {
        if (data?.emailChanged) {
            toast.success(t('userUpdateSuccessWithEmailChange'));
        } else {
            toast.success(t('userUpdatedSuccess'));
        }
        queryClient.invalidateQueries({ queryKey: ['users'] });
        onClose(); // Close modal on success
    },
    onError: (error: Error) => toast.error(error.message || t('unexpectedError')),
  });

  const handleSaveAll = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditMode) {
      // Edit mode: collect all fields and call single mutation
      const allData: Partial<User> & { id: string } = {
        id: user.id,
        name, role, teCode, countryCode, phone, city, email,
      };
      if (password) {
        if (password.length < 6) {
          toast.error(t('weakPasswordError'));
          return;
        }
        allData.password = password;
      }
      updateUserMutation.mutate(allData);
    } else if (onSave) {
      // Create mode
      if (!email || !password) {
        toast.error(t('fillRequiredFieldsError'));
        return;
      }
      onSave({ name, email, password, role, teCode, countryCode, phone, city });
    }
  };
  
  const isProcessing = (isEditMode && updateUserMutation.isPending) || (!isEditMode && isSaving);

  return (
    <form onSubmit={handleSaveAll} className="space-y-6">
        <div className="space-y-5">
          <h3 className="text-lg font-semibold text-secondary-700 flex items-center gap-2 border-b pb-3">
            <UserCog className="w-5 h-5 text-primary-500" />
            {t('accountDetails')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-secondary-700 mb-1">{t('name')}*</label>
                <div className="relative">
                  <div className={`absolute inset-y-0 ${dir === 'ltr' ? 'left-3' : 'right-3'} flex items-center pointer-events-none`}><UserPlus size={18} className="text-secondary-400" /></div>
                  <input type="text" id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('fullNamePlaceholder')} required className={`w-full py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${dir === 'ltr' ? 'pl-10 pr-4' : 'pr-10 pl-4'}`} />
                </div>
              </div>
              <div>
                <label htmlFor="teCode" className="block text-sm font-medium text-secondary-700 mb-1">{t('teCode')}</label>
                <div className="relative">
                  <div className={`absolute inset-y-0 ${dir === 'ltr' ? 'left-3' : 'right-3'} flex items-center pointer-events-none`}><Tag size={18} className="text-secondary-400" /></div>
                  <input type="text" id="teCode" value={teCode} onChange={(e) => setTeCode(e.target.value)} placeholder={t('teCodePlaceholder')} className={`w-full py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${dir === 'ltr' ? 'pl-10 pr-4' : 'pr-10 pl-4'}`} />
                </div>
              </div>
              <div>
                  <label htmlFor="city" className="block text-sm font-medium text-secondary-700 mb-1">{t('cityLabel')}</label>
                  <div className="relative">
                      <div className={`absolute inset-y-0 ${dir === 'ltr' ? 'left-3' : 'right-3'} flex items-center pointer-events-none`}><MapPin size={18} className="text-secondary-400" /></div>
                      <input type="text" id="city" value={city} onChange={(e) => setCity(e.target.value)} placeholder={t('cityPlaceholder')} className={`w-full py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${dir === 'ltr' ? 'pl-10 pr-4' : 'pr-10 pl-4'}`} />
                  </div>
              </div>
               <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1" htmlFor="phone">{t('phone')}</label>
                <div className="flex">
                  <select id="countryCode" value={countryCode} onChange={(e) => setCountryCode(e.target.value)} className="appearance-none rounded-s-lg rtl:rounded-s-none rtl:rounded-e-lg border border-e-0 rtl:border-e-auto rtl:border-s-0 border-gray-300 bg-gray-100 px-3 py-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors">
                    {COUNTRY_CODES.map((c) => (<option key={c.code} value={c.code}>{c.code}</option>))}
                  </select>
                  <div className="relative flex-1">
                      <div className={`absolute inset-y-0 ${dir === 'ltr' ? 'left-3' : 'right-3'} flex items-center pointer-events-none`}><Phone size={18} className="text-gray-400" /></div>
                      <input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t('phonePlaceholder')} className={`w-full rounded-e-lg rtl:rounded-e-none rtl:rounded-s-lg border border-gray-300 py-3 ps-10 pe-4 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200`} />
                  </div>
                </div>
              </div>
              <div className="md:col-span-2">
                <label htmlFor="role" className="block text-sm font-medium text-secondary-700 mb-1">{t('role')}*</label>
                <select id="role" value={role} onChange={(e) => setRole(e.target.value as UserRole)} className="w-full px-4 py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white">
                  {Object.values(UserRole).map((r) => (<option key={r} value={r}>{r}</option>))}
                </select>
              </div>

              <div className="md:col-span-2 pt-4 border-t">
                 <label htmlFor="email" className="block text-sm font-medium text-secondary-700 mb-1">{t('email')}*</label>
                  <div className="relative">
                    <div className={`absolute inset-y-0 ${dir === 'ltr' ? 'left-3' : 'right-3'} flex items-center pointer-events-none`}><Mail size={18} className="text-secondary-400" /></div>
                    <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('emailPlaceholder')} required className={`w-full py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`} />
                  </div>
                  {isEditMode && <p className="text-xs text-secondary-500 mt-1">{t('adminEmailChangeWarning')}</p>}
              </div>

              <div className="md:col-span-2">
                <label htmlFor="password" className="block text-sm font-medium text-secondary-700 mb-1">{t('password')}{isEditMode ? '' : '*'}</label>
                <div className="relative">
                  <div className={`absolute inset-y-0 ${dir === 'ltr' ? 'left-3' : 'right-3'} flex items-center pointer-events-none`}><Lock size={18} className="text-secondary-400" /></div>
                  <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={isEditMode ? t('passwordOptional') : t('passwordPlaceholder')} required={!isEditMode} className={`w-full py-2.5 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`} />
                </div>
                {isEditMode && <p className="text-xs text-secondary-500 mt-1">{t('passwordHint')}</p>}
              </div>

          </div>
        </div>

      {/* Main Action buttons */}
       <div className="flex justify-end pt-6 border-t gap-3">
         <Button type="button" variant="outline" onClick={onClose} disabled={isProcessing}>{t('cancel')}</Button>
         <Button type="submit" disabled={isProcessing} isLoading={isProcessing} leftIcon={<Save className="w-4 h-4" />}>
           {isEditMode ? t('saveChanges') : t('createUser')}
         </Button>
      </div>
    </form>
  )
}


// Main Users Page Component
const UsersPage: FC = () => {
  const { user: loggedInUser } = useAuth()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')

  const {
    data: users = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.getUsers(),
    enabled: loggedInUser?.role === UserRole.Admin,
  })
  
  const createUserMutation = useMutation({
    mutationFn: (userData: Partial<User>) => api.createUser(userData),
    onSuccess: () => {
      toast.success(t('userCreatedSuccess'));
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsModalOpen(false);
      setEditingUser(null);
    },
    onError: (error: any) => {
        let message = t('unexpectedError');
        if (error.code) {
            switch (error.code) {
                case 'auth/email-already-in-use': message = t('emailInUseError'); break;
                case 'auth/invalid-email': message = t('invalidEmailError'); break;
                case 'auth/weak-password': message = t('weakPasswordError'); break;
                default: message = error.message || `${t('unexpectedError')} (Code: ${error.code})`;
            }
        } else if (error.message) {
             if (error.message.includes("already in use")) {
                 message = t('emailInUseError');
             } else {
                 message = error.message;
             }
        }
        toast.error(message);
    },
  });
  
  const toggleLockMutation = useMutation({
    mutationFn: ({ userId, isLocked }: { userId: string; isLocked: boolean }) =>
      api.updateUser(userId, { isLocked: !isLocked }),
    onSuccess: (_, { isLocked }) => {
      toast.success(isLocked ? t('userUnlockedSuccess') : t('userLockedSuccess'));
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (_, { isLocked }) => {
      toast.error(isLocked ? t('userUnlockError') : t('userLockError'));
    },
  });

  const verifyMutation = useMutation({
    mutationFn: (userId: string) =>
      api.updateUser(userId, {
        isVerified: true,
      }),
    onSuccess: () => {
      toast.success(t('userVerifiedSuccess'))
      queryClient.invalidateQueries({
        queryKey: ['users'],
      })
    },
    onError: () => {
      toast.error(t('userVerifiedError'))
    },
  })
  
  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => api.deleteUser(userId),
    onSuccess: () => {
        toast.success(t('userDeletedSuccess'));
        queryClient.invalidateQueries({ queryKey: ['users'] });
        setDeletingUser(null);
    },
    onError: (error: Error) => {
        toast.error(error.message || t('userDeleteError'));
        setDeletingUser(null);
    }
  });


  const handleAddUser = () => {
    setEditingUser(null)
    setIsModalOpen(true)
  }
  const handleEditUser = (userToEdit: User) => {
    setEditingUser(userToEdit)
    setIsModalOpen(true)
  }
  
  const handleToggleLock = (user: User) => {
    toggleLockMutation.mutate({ userId: user.id, isLocked: !!user.isLocked });
  };
  
  const handleDeleteUser = (userToDelete: User) => {
    setDeletingUser(userToDelete);
  };

  const handleSaveUser = (userData: Partial<User>) => {
    // This handler is now only for creating users
    if (!userData.id) {
      createUserMutation.mutate(userData);
    }
  }

  // Filter users based on search term and role filter
  const filteredUsers = useMemo(() => {
    if (!users) return []
    return users.filter((user: User) => {
      const matchesSearch =
        searchTerm === '' ||
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.phone && user.phone.includes(searchTerm))
      const matchesRole = roleFilter === 'all' || user.role === roleFilter
      return matchesSearch && matchesRole
    })
  }, [users, searchTerm, roleFilter])
  // Check if user is admin
  if (loggedInUser?.role !== UserRole.Admin) {
    return <Navigate to="/dashboard" />
  }
  // Helper function to get role badge color
  const getRoleChipColor = (role: UserRole) => {
    switch (role) {
      case UserRole.Admin:
        return 'bg-primary-500 text-white'
      case UserRole.Staff:
        return 'bg-primary-200 text-primary-800'
      case UserRole.Client:
        return 'bg-secondary-200 text-secondary-800'
      default:
        return 'bg-secondary-100 text-secondary-800'
    }
  }
  // Loading skeleton
  const TableSkeleton = () => (
    <div className="overflow-x-auto">
      <div className="bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
        <div className="animate-pulse">
          <div className="h-12 bg-secondary-100 flex items-center px-6">
            <div className="h-4 bg-secondary-200 rounded w-1/6"></div>
            <div className="h-4 bg-secondary-200 rounded w-1/6 ml-auto"></div>
          </div>
          <div className="p-6 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex space-x-4">
                <div className="h-4 bg-secondary-200 rounded w-1/4"></div>
                <div className="h-4 bg-secondary-200 rounded w-1/4"></div>
                <div className="h-4 bg-secondary-200 rounded w-1/6"></div>
                <div className="h-4 bg-secondary-200 rounded w-1/6"></div>
                <div className="h-4 bg-secondary-200 rounded w-1/6"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-secondary-900 mb-2">
            {t('userManagement')}
          </h1>
          <p className="text-secondary-600 max-w-3xl">
            {t('userManagementDesc')}
          </p>
        </div>
        {/* Filters and Actions */}
        <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1 flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none rtl:left-auto rtl:right-0 rtl:pr-3">
                  <Search className="h-5 w-5 text-secondary-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('searchUsers')}
                  className="block w-full pl-10 pr-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent rtl:pr-10 rtl:pl-3"
                />
              </div>
              {/* Role Filter */}
              <div className="w-full sm:w-auto">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none rtl:left-auto rtl:right-0 rtl:pr-3">
                    <Filter className="h-4 w-4 text-secondary-400" />
                  </div>
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="block w-full pl-10 pr-10 py-2 text-base border border-secondary-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white rtl:pr-10 rtl:pl-10"
                  >
                    <option value="all">{t('allRoles')}</option>
                    {Object.values(UserRole).map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none rtl:right-auto rtl:left-0 rtl:pl-2">
                    <ChevronDown className="h-4 w-4 text-secondary-400" />
                  </div>
                </div>
              </div>
            </div>
            {/* Add User Button */}
            <button
              onClick={handleAddUser}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors shadow-sm"
            >
              <UserPlus className="w-5 h-5" />
              <span>{t('addNewUser')}</span>
            </button>
          </div>
        </div>
        {/* Error Message */}
        {isError && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-500" />
            <p>
              {t('errorFetchingUsers')}
            </p>
          </div>
        )}
        {/* Users Table/Cards */}
        {isLoading ? (
          <TableSkeleton />
        ) : (
          <div className="bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200">
                <thead className="bg-secondary-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-start text-xs font-medium text-secondary-500 uppercase tracking-wider"
                    >
                      {t('name')}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-start text-xs font-medium text-secondary-500 uppercase tracking-wider"
                    >
                      {t('emailStatus')}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-start text-xs font-medium text-secondary-500 uppercase tracking-wider"
                    >
                      {t('phone')}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-start text-xs font-medium text-secondary-500 uppercase tracking-wider"
                    >
                      {t('role')}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-center text-xs font-medium text-secondary-500 uppercase tracking-wider"
                    >
                      {t('verified')}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-end text-xs font-medium text-secondary-500 uppercase tracking-wider"
                    >
                      {t('actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-200">
                  {filteredUsers.map((user: User) => (
                    <tr
                      key={user.id}
                      className="hover:bg-secondary-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-start">
                        <div className="text-sm font-medium text-secondary-900 flex items-center">
                          {user.name}
                          {user.isLocked && <Lock className="w-3 h-3 ms-2 text-secondary-500" />}
                        </div>
                        <div className="text-xs text-secondary-500">
                          {user.city || t('notProvided')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-start">
                        <div className="text-sm text-secondary-600">
                          {user.email}
                        </div>
                        <div className="flex items-center mt-1">
                          {user.isEmailVerified ? (
                            <span className="inline-flex items-center text-xs text-primary-600">
                              <CheckCircle className="w-3 h-3 me-1" />
                              {t('verified')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-xs text-secondary-600">
                              <XCircle className="w-3 h-3 me-1" />
                              {t('emailNotVerified')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-start">
                        {user.phone ? (
                          <div>
                            <div className="text-sm text-secondary-600 font-mono">
                              {user.countryCode} {user.phone}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-secondary-400">
                            {t('notProvided')}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-start">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleChipColor(user.role)}`}
                        >
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {user.isVerified ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                            <CheckCircle className="w-3 h-3 me-1" />
                            {t('verified')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary-200 text-secondary-800">
                            <Clock className="w-3 h-3 me-1" />
                            {t('pendingVerification')}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-end text-sm font-medium">
                        <div className="flex items-center justify-end space-x-3 rtl:space-x-reverse">
                           <button
                            onClick={() => handleDeleteUser(user)}
                            className="text-rose-600 hover:text-rose-900 flex items-center"
                          >
                            <Trash2 className="w-4 h-4 me-1" />
                            {t('delete')}
                          </button>
                          <button
                            onClick={() => handleEditUser(user)}
                            className="text-primary-600 hover:text-primary-900 flex items-center"
                          >
                            <Edit className="w-4 h-4 me-1" />
                            {t('edit')}
                          </button>
                          <button
                            onClick={() => handleToggleLock(user)}
                            className={`flex items-center ${user.isLocked ? 'text-emerald-600 hover:text-emerald-900' : 'text-secondary-600 hover:text-secondary-900'}`}
                          >
                            {user.isLocked ? (
                              <Unlock className="w-4 h-4 me-1" />
                            ) : (
                              <Lock className="w-4 h-4 me-1" />
                            )}
                            {user.isLocked ? t('unlock') : t('lock')}
                          </button>
                          {!user.isVerified &&
                            user.role === UserRole.Client && (
                              <button
                                onClick={() => verifyMutation.mutate(user.id)}
                                disabled={verifyMutation.isPending}
                                className="text-primary-600 hover:text-primary-900 flex items-center disabled:opacity-50"
                              >
                                <UserCheck className="w-4 h-4 me-1" />
                                {t('verify')}
                              </button>
                            )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Empty state */}
              {filteredUsers.length === 0 && (
                <div className="text-center py-12">
                  <div className="mx-auto h-12 w-12 text-secondary-400 rounded-full bg-secondary-100 flex items-center justify-center mb-4">
                    <UserCog className="h-6 w-6" />
                  </div>
                  <h3 className="mt-2 text-sm font-medium text-secondary-900">
                    {t('noUsersFound')}
                  </h3>
                  <p className="mt-1 text-sm text-secondary-500">
                    {searchTerm || roleFilter !== 'all'
                      ? t('noUsersMatchFilter')
                      : t('noUsersYet')}
                  </p>
                  {(searchTerm || roleFilter !== 'all') && (
                    <div className="mt-6">
                      <button
                        type="button"
                        className="inline-flex items-center px-4 py-2 border border-secondary-300 shadow-sm text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        onClick={() => {
                          setSearchTerm('')
                          setRoleFilter('all')
                        }}
                      >
                        {t('clearFilters')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-secondary-200">
              {filteredUsers.map((user: User) => (
                <div key={user.id} className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-sm font-medium text-secondary-900 flex items-center">
                        {user.name}
                        {user.isLocked && <Lock className="w-3 h-3 ms-2 text-secondary-500" />}
                      </div>
                      {user.teCode && (
                        <div className="text-xs text-secondary-500">
                          {user.teCode}
                        </div>
                      )}
                    </div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleChipColor(user.role)}`}
                    >
                      {user.role}
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
                    <div className="flex items-center text-secondary-500">
                        <MapPin className="w-4 h-4 me-2 text-secondary-400" />
                        {user.city || t('notProvided')}
                    </div>
                    <div className="flex items-center text-secondary-500">
                      <Mail className="w-4 h-4 me-2 text-secondary-400" />
                      {user.email}
                    </div>
                    <div className="flex items-center text-secondary-500">
                      {user.isEmailVerified ? (
                        <CheckCircle className="w-4 h-4 me-1 text-primary-500" />
                      ) : (
                        <XCircle className="w-4 h-4 me-1 text-secondary-500" />
                      )}
                      {user.isEmailVerified
                        ? t('emailVerified')
                        : t('emailNotVerified')}
                    </div>
                    <div className="flex items-center text-secondary-500">
                      <Phone className="w-4 h-4 me-2 text-secondary-400" />
                      {user.phone
                        ? `${user.countryCode} ${user.phone}`
                        : t('notProvided')}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center text-secondary-500 text-sm">
                    <ShieldCheck className="w-4 h-4 me-2 text-secondary-400" />
                    {user.isVerified
                      ? t('accountVerified')
                      : t('accountPending')}
                  </div>
                  <div className="mt-4 flex justify-end space-x-3 rtl:space-x-reverse border-t border-secondary-200 pt-3">
                    <button
                      onClick={() => handleDeleteUser(user)}
                      className="text-rose-600 hover:text-rose-900 text-sm font-medium flex items-center"
                    >
                      <Trash2 className="w-4 h-4 me-1" />
                       {t('delete')}
                    </button>
                    <button
                      onClick={() => handleEditUser(user)}
                      className="text-primary-600 hover:text-primary-900 text-sm font-medium flex items-center"
                    >
                      <Edit className="w-4 h-4 me-1" />
                      {t('edit')}
                    </button>
                    <button
                      onClick={() => handleToggleLock(user)}
                      className={`text-sm font-medium flex items-center ${user.isLocked ? 'text-emerald-600 hover:text-emerald-900' : 'text-secondary-600 hover:text-secondary-900'}`}
                    >
                       {user.isLocked ? (
                          <Unlock className="w-4 h-4 me-1" />
                        ) : (
                          <Lock className="w-4 h-4 me-1" />
                        )}
                        {user.isLocked ? t('unlock') : t('lock')}
                    </button>
                    {!user.isVerified && user.role === UserRole.Client && (
                      <button
                        onClick={() => verifyMutation.mutate(user.id)}
                        disabled={verifyMutation.isPending}
                        className="text-primary-600 hover:text-primary-900 text-sm font-medium flex items-center disabled:opacity-50"
                      >
                        <UserCheck className="w-4 h-4 me-1" />
                        {t('verify')}
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {/* Mobile empty state */}
              {filteredUsers.length === 0 && (
                <div className="text-center py-12 px-4">
                  <div className="mx-auto h-12 w-12 text-secondary-400 rounded-full bg-secondary-100 flex items-center justify-center mb-4">
                    <UserCog className="h-6 w-6" />
                  </div>
                  <h3 className="mt-2 text-sm font-medium text-secondary-900">
                    {t('noUsersFound')}
                  </h3>
                  <p className="mt-1 text-sm text-secondary-500">
                    {searchTerm || roleFilter !== 'all'
                      ? t('noUsersMatchFilter')
                      : t('noUsersYet')}
                  </p>
                  {(searchTerm || roleFilter !== 'all') && (
                    <div className="mt-6">
                      <button
                        type="button"
                        className="inline-flex items-center px-4 py-2 border border-secondary-300 shadow-sm text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        onClick={() => {
                          setSearchTerm('')
                          setRoleFilter('all')
                        }}
                      >
                        {t('clearFilters')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {/* Add/Edit User Modal */}
      {isModalOpen && (
        <Modal
          title={
            editingUser
              ? t('editUser')
              : t('addNewUser')
          }
          onClose={() => setIsModalOpen(false)}
          size="2xl"
        >
          <UserForm
            key={editingUser ? editingUser.id : 'new'} // Key ensures form resets on user change
            user={editingUser}
            onSave={handleSaveUser}
            onClose={() => setIsModalOpen(false)}
            isSaving={createUserMutation.isPending}
          />
        </Modal>
      )}
      {/* Delete User Confirmation Modal */}
      <AlertModal
        isOpen={!!deletingUser}
        onClose={() => setDeletingUser(null)}
        title={t('deleteUserConfirmTitle')}
        type="error"
        confirmLabel={t('delete')}
        cancelLabel={t('cancel')}
        isLoading={deleteUserMutation.isPending}
        onConfirm={() => {
            if (deletingUser) {
                deleteUserMutation.mutate(deletingUser.id);
            }
        }}
      >
        {t('deleteUserConfirmText', { name: deletingUser?.name })}
      </AlertModal>
    </div>
  )
}
export default UsersPage;